import AddressAutocomplete from './../common/AddressAutocomplete';
import axios from 'axios';
import React, { useState, useEffect, useRef } from 'react';
import { apiClient, showToast } from '../../utils/apiClient';
import { API } from '../../config';
import { formatDate, formatDateTime, formatNotesWithBullets, getDayOfWeek, formatDateWithDay, getLocationDisplay, getLocationChipColor, getRowAlert, formatDatetimeForInput, parseUTCDatetime, getTodayInAEST, isPrepaymentApproaching, hasAppointments, getAppointmentSummary } from '../../utils/helpers';
import { getAvailableUrns, getAvailableKeepsakes, calculateBaseServicePrice, getProductPrice, getCustomProductIcon, getProductIcon, formatKeepsakesForDisplay, parseQuantityFromString, convertToCartFormat, calculateTotalCost, initializePricing } from '../../utils/pricingHelpers';
import { SearchableMultiSelect, VetClinicCombobox } from '../forms/FormUtils';
import MultiItemSelector from '../forms/MultiItemSelector';

const WalkInFormModal = ({ onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [existingPetId, setExistingPetId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [formData, setFormData] = useState({
    client_name: '',
    phone_number: '',
    email: '',
    address: '',
    postcode: '',
    pet_name: '',
    pet_type: 'Dog',
    sex: 'M',
    breed: '',
    weight_kg: '10',
    age_years: '',
    payment_method: 'Online',
    referral_source: 'Vet',
    vet_clinic: '',
    notes: ''
  });
  
  const [showVetClinic, setShowVetClinic] = useState(true);
  const [vetClinics, setVetClinics] = useState([]);
  
  // Signature canvas state
  const signatureCanvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  
  useEffect(() => {
    // Load vet clinics
    const loadVetClinics = async () => {
      try {
        const response = await apiClient.get('/config/vet-clinics');
        setVetClinics(response.data.vet_clinics || []);
      } catch (error) {
        console.error('Failed to load vet clinics:', error);
      }
    };
    loadVetClinics();
  }, []);

  // Search existing pets
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const response = await apiClient.get(`/pets?search=${encodeURIComponent(searchQuery)}`);
        const pets = Array.isArray(response.data) ? response.data : response.data.pets || [];
        setSearchResults(pets.slice(0, 8));
      } catch { setSearchResults([]); }
      finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const selectExistingPet = (pet) => {
    setExistingPetId(pet.id);
    const petType = String(pet.pet_type || 'Dog');
    const cleanType = petType.includes('.') ? petType.split('.').pop() : petType;
    // Check if returning client (has referral source indicating repeat, or multiple records exist)
    const isReturning = pet.referral_source === 'Returning Client' || pet.referral_source === 'Repeated Customer';
    setFormData({
      client_name: pet.client_name || '',
      phone_number: pet.phone_number || '',
      email: pet.email || '',
      address: pet.address || '',
      postcode: pet.postcode || '',
      pet_name: pet.pet_name || '',
      pet_type: cleanType.charAt(0).toUpperCase() + cleanType.slice(1).toLowerCase(),
      sex: pet.sex || 'M',
      breed: pet.breed || '',
      weight_kg: String(pet.weight_kg || '10'),
      age_years: pet.age_years ? String(pet.age_years) : '',
      payment_method: pet.payment_method || 'Online',
      referral_source: pet.referral_source || 'Vet',
      vet_clinic: pet.vet_clinic || '',
      notes: pet.jewelry_notes || '',
      _isReturning: isReturning
    });
    setShowVetClinic(pet.referral_source === 'Vet');
    setSearchQuery('');
    setSearchResults([]);
    setShowSearch(false);
    const firstName = (pet.client_name || '').split(' ')[0];
    showToast(`Loaded ${pet.pet_name} (${firstName})`);
  };

  const clearExistingPet = () => {
    setExistingPetId(null);
    setFormData({
      client_name: '', phone_number: '', email: '', address: '', postcode: '',
      pet_name: '', pet_type: 'Dog', sex: 'M', breed: '', weight_kg: '10',
      age_years: '', payment_method: 'Online', referral_source: 'Vet', vet_clinic: '', notes: ''
    });
    setShowVetClinic(true);
  };

  const handleReferralChange = (value) => {
    setFormData({ ...formData, referral_source: value });
    setShowVetClinic(value === 'Vet');
  };


  // Signature canvas functions
  const startDrawing = (e) => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;    // Account for canvas scaling
    const scaleY = canvas.height / rect.height;  // Account for canvas scaling
    
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;
    
    const ctx = canvas.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
    setHasSignature(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    
    e.preventDefault(); // Prevent scrolling while drawing
    
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;    // Account for canvas scaling
    const scaleY = canvas.height / rect.height;  // Account for canvas scaling
    
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;
    
    const ctx = canvas.getContext('2d');
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#1e40af'; // Blue color
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submitData = {
        client_name: formData.client_name,
        phone_number: formData.phone_number,
        email: formData.email,
        address: formData.address,
        postcode: formData.postcode,
        vet_clinic: showVetClinic ? formData.vet_clinic : null,
        pet_name: formData.pet_name,
        pet_type: formData.pet_type,
        sex: formData.sex,
        breed: formData.breed,
        weight_kg: parseFloat(formData.weight_kg) || 10,
        age_years: formData.age_years ? parseInt(formData.age_years) : null,
        referral_source: formData.referral_source,
        payment_method: formData.payment_method,
      };

      // Add notes to appropriate field based on content
      if (formData.notes) {
        submitData.jewelry_notes = formData.notes;
      }

      if (existingPetId) {
        // Update existing record — don't overwrite status, location, or workflow fields
        await apiClient.put(`/pets/${existingPetId}`, submitData);
        showToast('✅ Record updated and signed successfully!');
      } else {
        // Create new record
        submitData.arrival_date = getTodayInAEST();
        submitData.location_area = 'Not Yet in Our Care';
        submitData.status = 'Pending in our care SMS';
        submitData.keepsakes_selected = [];
        submitData.urns_selected = [];
        submitData.jewelry_selected = [];
        submitData.transportation_fees = [];
        submitData.certificate_type = 'Digital';
        await apiClient.post('/pets', submitData);
        showToast('✅ Booking received successfully!');
      }
      onSuccess();
    } catch (error) {
      console.error('Walk-in form submission failed:', error);
      if (error.response?.status === 422) {
        const errorDetail = error.response?.data?.detail;
        const errorMessage = typeof errorDetail === 'string' 
          ? errorDetail 
          : JSON.stringify(errorDetail, null, 2);
        alert(`❌ Validation Error:\n\n${errorMessage}`);
      } else {
        alert('❌ Failed to submit booking. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 rounded-xl max-w-3xl w-full max-h-[95vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-purple-600 p-6 border-b z-10 rounded-t-xl">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-white">
              📝 Welcome to HOLONS
            </h2>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <p className="text-white text-sm mt-2 opacity-90">Please fill in your details below to book our service</p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Staff: Search Existing Customer */}
          <div className="bg-white rounded-lg p-4 shadow-md border-l-4 border-blue-500" data-testid="existing-customer-search">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                Existing booking?
              </h3>
              {existingPetId ? (
                <button type="button" onClick={clearExistingPet}
                  className="text-xs text-red-600 hover:underline">
                  Clear &amp; start fresh
                </button>
              ) : (
                <button type="button" onClick={() => setShowSearch(!showSearch)}
                  className="text-xs text-blue-600 hover:underline">
                  {showSearch ? 'Hide search' : 'Search existing'}
                </button>
              )}
            </div>
            {existingPetId && (() => {
              const firstName = (formData.client_name || '').split(' ')[0];
              const isReturning = formData._isReturning;
              return (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
                  <p className="font-semibold">{isReturning ? `Welcome back, ${firstName}!` : `Welcome, ${firstName}!`}</p>
                  <p className="mt-0.5 text-green-700">We have your details on file for <strong>{formData.pet_name}</strong>. Please review the information below, make any changes if needed, and sign at the bottom.</p>
                </div>
              );
            })()}
            {showSearch && !existingPetId && (
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by pet name, client name, or phone..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  autoFocus
                  data-testid="existing-pet-search-input"
                />
                {searching && <p className="text-xs text-gray-400 mt-1">Searching...</p>}
                {searchResults.length > 0 && (
                  <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {searchResults.map(pet => (
                      <button
                        key={pet.id}
                        type="button"
                        onClick={() => selectExistingPet(pet)}
                        className="w-full text-left px-3 py-2.5 hover:bg-blue-50 border-b border-gray-100 last:border-0"
                        data-testid={`search-result-${pet.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="font-semibold text-gray-900 text-sm">{pet.pet_name}</span>
                            <span className="text-gray-500 text-sm ml-2">{pet.client_name}</span>
                          </div>
                          <span className="text-xs text-gray-400">{pet.phone_number}</span>
                        </div>
                        <p className="text-xs text-gray-400">{pet.breed} · {pet.weight_kg}kg · {pet.referral_source}</p>
                      </button>
                    ))}
                  </div>
                )}
                {searchQuery.length >= 2 && !searching && searchResults.length === 0 && (
                  <p className="text-xs text-gray-400 mt-1">No matching records found. Fill in the form below for a new booking.</p>
                )}
              </div>
            )}
          </div>

          {/* Client Information */}
          <div className="bg-white rounded-lg p-6 shadow-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <span className="bg-blue-100 text-blue-600 rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm font-bold">1</span>
              Your Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                <input
                  type="text"
                  required
                  value={formData.client_name}
                  onChange={(e) => {
                    const value = e.target.value;
                    const capitalized = value.replace(/\b\w/g, l => l.toUpperCase());
                    setFormData({ ...formData, client_name: capitalized });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
                <input
                  type="tel"
                  required
                  value={formData.phone_number}
                  onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Address *</label>
                <AddressAutocomplete
                  value={formData.address}
                  onChange={(value) => setFormData(prev => ({ ...prev, address: value }))}
                  onPostcodeChange={(postcode) => setFormData(prev => ({ ...prev, postcode: postcode }))}
                  placeholder="Start typing address..."
                  required={true}
                />
                <p className="text-xs text-gray-500 mt-1">Postcode will be automatically filled from address</p>
              </div>
            </div>
          </div>

          {/* Pet Information */}
          <div className="bg-white rounded-lg p-6 shadow-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <span className="bg-purple-100 text-purple-600 rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm font-bold">2</span>
              Pet Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Pet Name *</label>
                <input
                  type="text"
                  required
                  value={formData.pet_name}
                  onChange={(e) => {
                    const value = e.target.value;
                    const capitalized = value.toUpperCase();
                    setFormData({ ...formData, pet_name: capitalized });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Pet Type *</label>
                <select
                  required
                  value={formData.pet_type}
                  onChange={(e) => setFormData({ ...formData, pet_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="Dog">Dog</option>
                  <option value="Cat">Cat</option>
                  <option value="Exotic Pet">Exotic Pet</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sex *</label>
                <select
                  required
                  value={formData.sex}
                  onChange={(e) => setFormData({ ...formData, sex: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="M">Male</option>
                  <option value="F">Female</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Breed *</label>
                <input
                  type="text"
                  required
                  value={formData.breed}
                  onChange={(e) => setFormData({ ...formData, breed: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Weight (kg) *</label>
                <select
                  required
                  value={formData.weight_kg}
                  onChange={(e) => setFormData({ ...formData, weight_kg: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select weight...</option>
                  {/* Exotic pets (very small) */}
                  <option value="0.1">0.1 kg</option>
                  <option value="0.2">0.2 kg</option>
                  <option value="0.5">0.5 kg</option>
                  {/* Small to large pets - sequential every 1kg */}
                  <option value="1">1 kg</option>
                  <option value="2">2 kg</option>
                  <option value="3">3 kg</option>
                  <option value="4">4 kg</option>
                  <option value="5">5 kg</option>
                  <option value="6">6 kg</option>
                  <option value="7">7 kg</option>
                  <option value="8">8 kg</option>
                  <option value="9">9 kg</option>
                  <option value="10">10 kg</option>
                  <option value="11">11 kg</option>
                  <option value="12">12 kg</option>
                  <option value="13">13 kg</option>
                  <option value="14">14 kg</option>
                  <option value="15">15 kg</option>
                  <option value="16">16 kg</option>
                  <option value="17">17 kg</option>
                  <option value="18">18 kg</option>
                  <option value="19">19 kg</option>
                  <option value="20">20 kg</option>
                  <option value="21">21 kg</option>
                  <option value="22">22 kg</option>
                  <option value="23">23 kg</option>
                  <option value="24">24 kg</option>
                  <option value="25">25 kg</option>
                  <option value="26">26 kg</option>
                  <option value="27">27 kg</option>
                  <option value="28">28 kg</option>
                  <option value="29">29 kg</option>
                  <option value="30">30 kg</option>
                  <option value="31">31 kg</option>
                  <option value="32">32 kg</option>
                  <option value="33">33 kg</option>
                  <option value="34">34 kg</option>
                  <option value="35">35 kg</option>
                  <option value="36">36 kg</option>
                  <option value="37">37 kg</option>
                  <option value="38">38 kg</option>
                  <option value="39">39 kg</option>
                  <option value="40">40 kg</option>
                  <option value="41">41 kg</option>
                  <option value="42">42 kg</option>
                  <option value="43">43 kg</option>
                  <option value="44">44 kg</option>
                  <option value="45">45 kg</option>
                  <option value="46">46 kg</option>
                  <option value="47">47 kg</option>
                  <option value="48">48 kg</option>
                  <option value="49">49 kg</option>
                  <option value="50">50 kg</option>
                  <option value="51">51 kg</option>
                  <option value="52">52 kg</option>
                  <option value="53">53 kg</option>
                  <option value="54">54 kg</option>
                  <option value="55">55 kg</option>
                  <option value="56">56 kg</option>
                  <option value="57">57 kg</option>
                  <option value="58">58 kg</option>
                  <option value="59">59 kg</option>
                  <option value="60">60 kg</option>
                  <option value="61">61 kg</option>
                  <option value="62">62 kg</option>
                  <option value="63">63 kg</option>
                  <option value="64">64 kg</option>
                  <option value="65">65 kg</option>
                  <option value="66">66 kg</option>
                  <option value="67">67 kg</option>
                  <option value="68">68 kg</option>
                  <option value="69">69 kg</option>
                  <option value="70">70 kg</option>
                  <option value="71">71+ kg</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Age (years)</label>
                <input
                  type="number"
                  min="0"
                  max="50"
                  value={formData.age_years}
                  onChange={(e) => setFormData({ ...formData, age_years: e.target.value })}
                  placeholder="Optional"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Service Information */}
          <div className="bg-white rounded-lg p-6 shadow-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <span className="bg-pink-100 text-pink-600 rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm font-bold">3</span>
              Service Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method *</label>
                <select
                  required
                  value={formData.payment_method}
                  onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="Online">Online</option>
                  <option value="VetPay">VetPay</option>
                  <option value="EFTPOS">EFTPOS</option>
                  <option value="Cash">Cash</option>
                  <option value="Invoice">Invoice</option>
                  <option value="Afterpay">Afterpay</option>
                  <option value="Clinic Account">Clinic Account</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">How did you hear about us? *</label>
                <select
                  required
                  value={formData.referral_source}
                  onChange={(e) => handleReferralChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="Vet">Vet</option>
                  <option value="Google">Google</option>
                  <option value="Facebook">Facebook</option>
                  <option value="Unknown">Unknown</option>
                  <option value="Van">Van</option>
                  <option value="Billboard">Billboard</option>
                  <option value="Family/Friend">Family/Friend</option>
                  <option value="Returning Client">Returning Client</option>
                </select>
              </div>
              {showVetClinic && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Vet Clinic *</label>
                  <select
                    required={showVetClinic}
                    value={formData.vet_clinic}
                    onChange={(e) => setFormData({ ...formData, vet_clinic: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select a vet clinic...</option>
                    {vetClinics.map((clinic) => (
                      <option key={clinic} value={clinic}>{clinic}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Additional Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Any additional information..."
                />
              </div>
              
              {/* Signature Section */}
              <div className="md:col-span-2 mt-6">
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <p className="text-sm font-medium text-gray-700 mb-3">
                    I authorize HOLONS to perform Aqua Cremation for my pet.
                  </p>
                  
                  <label className="block text-sm font-medium text-gray-600 mb-2">Signature:</label>
                  
                  <div className="relative">
                    <canvas
                      ref={signatureCanvasRef}
                      width={600}
                      height={150}
                      onMouseDown={startDrawing}
                      onMouseMove={draw}
                      onMouseUp={stopDrawing}
                      onMouseLeave={stopDrawing}
                      onTouchStart={startDrawing}
                      onTouchMove={draw}
                      onTouchEnd={stopDrawing}
                      className="w-full border-2 border-teal-300 rounded-lg bg-white cursor-crosshair touch-none"
                      style={{ touchAction: 'none' }}
                    />
                  </div>
                  
                  {hasSignature && (
                    <button
                      type="button"
                      onClick={clearSignature}
                      className="mt-2 text-sm text-red-600 hover:text-red-800 font-medium"
                    >
                      Clear Signature
                    </button>
                  )}
                  
                  <p className="text-xs text-gray-500 mt-2 italic">
                    Sign above with your finger or mouse (optional)
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="flex justify-end space-x-4 pt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-8 py-3 border-2 border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium text-lg transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-8 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 font-medium text-lg disabled:opacity-50 disabled:cursor-not-allowed shadow-lg transition"
            >
              {loading ? 'Submitting...' : existingPetId ? 'Update & Confirm Booking' : 'Submit Booking'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Thank You Modal

export default WalkInFormModal;
